import { useState, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from "../../../components/Cards/Card";
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom"; // ✅ import useLocation
import { BASE_URL } from "../../../components/BaseUrl";
import { useTranslation } from 'react-i18next';

/* ------------ PASSWORD CHECKLIST HELPERS --------------- */
const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok ? "#16a34a" : showError ? "#d32f2f" : "#64748b";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: ok ? "none" : "2px solid #9aa4b2",
          background: ok ? "#22c55e" : "transparent",
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: "2px solid #fff",
              borderBottom: "2px solid #fff",
              transform: "rotate(45deg)",
            }}
          />
        ) : (
          <span style={{ position: "relative", width: 10, height: 10 }}>
            <span
              style={{
                position: "absolute",
                width: "100%",
                height: 2,
                background: "#9aa4b2",
                transform: "rotate(45deg)",
                top: "50%",
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: "absolute",
                width: "100%",
                height: 2,
                background: "#9aa4b2",
                transform: "rotate(-45deg)",
                top: "50%",
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};

const PasswordChecklist = ({ password, showError }) => {
      const { t } = useTranslation();
  
  const rules = useMemo(
    () => ({
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
    }),
    [password]
  );

  return (
 <Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
   <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
   <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
   <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
   <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
   <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
 </Box>
  );
};
/* ------------------------------------------------------- */

const ForgetSetPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation(); // ✅ to get email + code
  const { t ,i18n} = useTranslation();

  const { email, code } = location.state || {}; // ✅ destructure state

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (values) => {
    const errors = {};
    if (!values.password) {
      errors.password = "Password is required";
    } else {
      const pwd = values.password;
      const hasLen = pwd.length >= 8;
      const hasLower = /[a-z]/.test(pwd);
      const hasUpper = /[A-Z]/.test(pwd);
      const hasNum = /[0-9]/.test(pwd);
      const hasSpecial = /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(pwd);

      if (!(hasLen && hasLower && hasUpper && hasNum && hasSpecial)) {
        errors.password = "Password is too weak";
      }
    }
    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: email || "",
      password: "",
    },
    validate,
    onSubmit: async (values) => {
      const errors = validate(values);
      if (Object.keys(errors).length > 0) {
        Object.values(errors).forEach((err) => toast.error(err));
        return;
      }

      if (!email || !code) {
        toast.error("Missing verification details. Please try again.");
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await fetch(`${BASE_URL}/public/worker/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: values.email,
            code, // ✅ pass the OTP code from previous screen
            newPassword: values.password, // ✅ key must be newPassword
          }),
        });

        const data = await response.json();
        console.log("📥 Reset Password response:", data);

        if (!response.ok || data.error) {
          throw new Error(data.message || "Failed to reset password");
        }
const message =
i18n.language === "es"
? data.message_es
: data.message_en;
    toast.success(message || "Password reset successfully");
        setTimeout(() => {
          navigate("/");
        }, 1200);
      } catch (err) {
        console.error("🚨 Reset Password error:", err);
        toast.error(err.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", minHeight: "100vh", mt: isMobile ? 2 : 0, px: 1 }}>
      <Box
        sx={{
          py: isMobile ? 5 : 10,
          px: isMobile ? 0 : 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BaseCard
          maxWidth={isMobile ? "100%" : 380}
          sx={{
            width: isMobile ? "100%" : "auto",
            boxSizing: "border-box",
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                  {t("setPasswordTitle")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                  {t("setPasswordSubtitle")}
                </CustomText>
              </Box>

              {/* Email */}
              <label>{t("emailLabel")}<span style={{ color: "red" }}>*</span></label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                className="project_input"
                style={{ height: "42px" }}
              />

              {/* Password */}
              <label style={{ marginTop: "10px" }}>{t("passwordLabel")}<span style={{ color: "red" }}>*</span></label>
              <CustomPasswordInput
                 placeholder="********"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
                initiallyVisible={true}
              />

              {/* Password Strength Checklist */}
              <PasswordChecklist password={formik.values.password} showError={formik.touched.password} />

              <CustomButton
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isSubmitting && <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />}
                    {t("setPasswordButton")}
                  </Box>
                }
                type="submit"
                fullWidth
                sx={{ mt: 2 }}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default ForgetSetPassword;
